import React from 'react';
import { FiBox, FiPlus, FiEdit2, FiTrash2, FiDownload } from 'react-icons/fi';

interface ProjectInventoryTabProps {
  projectInventory: any[];
  isLoading?: boolean;
  setIsInventoryModalOpen: (val: boolean) => void;
  setIsImportModalOpen?: (val: boolean) => void;
  handleOpenEditInventoryModal: (item: any) => void;
  handleOpenDeleteInventoryModal: (item: any) => void;
}

export const ProjectInventoryTab: React.FC<ProjectInventoryTabProps> = ({
  projectInventory,
  isLoading,
  setIsInventoryModalOpen,
  setIsImportModalOpen,
  handleOpenEditInventoryModal,
  handleOpenDeleteInventoryModal,
}) => {
  return (
    <div className="space-y-6">
      {/* Header Row */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h3 className="text-white text-base font-bold">
          {isLoading ? 'Loading...' : `${projectInventory.length} Items`}
        </h3>
        <div className="flex items-center gap-3">
          {setIsImportModalOpen && (
            <button
              onClick={() => setIsImportModalOpen(true)}
              className="px-4 py-2 bg-[#18181B] hover:bg-[#27272A] border border-white/10 text-white rounded-full text-xs font-semibold transition-all flex items-center gap-1.5 cursor-pointer shadow-sm"
            >
              <FiDownload className="w-3.5 h-3.5 text-white" />
              <span>Import Inventory</span>
            </button>
          )}
          <button
            onClick={() => setIsInventoryModalOpen(true)}
            className="px-4 py-2 bg-[#18181B] hover:bg-[#27272A] border border-white/10 text-white rounded-full text-xs font-semibold transition-all flex items-center gap-1.5 cursor-pointer shadow-sm animate-pulse-none"
          >
            <FiPlus className="w-3.5 h-3.5 text-white" />
            <span>Add Item</span>
          </button>
        </div>
      </div>

      {isLoading ? (
        /* Skeleton loading list */
        <div className="space-y-3">
          {[1, 2, 3].map((n) => (
            <div
              key={n}
              className="bg-[#111111] border border-white/5 rounded-2xl p-4 flex items-center justify-between animate-pulse"
            >
              <div className="flex items-center gap-4 w-full">
                <div className="w-10 h-10 rounded-xl bg-[#1A1A1A] border border-white/5 shrink-0" />
                <div className="space-y-2 w-full max-w-[200px]">
                  <div className="h-4 bg-zinc-800 rounded w-3/4" />
                  <div className="h-3 bg-zinc-800 rounded w-1/2" />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="h-6 w-16 bg-zinc-800/80 rounded-full" />
                <div className="flex items-center gap-1.5">
                  <div className="w-7 h-7 rounded-full bg-zinc-850 border border-white/5" />
                  <div className="w-7 h-7 rounded-full bg-zinc-850 border border-white/5" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : projectInventory.length === 0 ? (
        /* Empty State */
        <div className="flex flex-col items-center justify-center rounded-2xl border border-white/5 bg-[#111111] p-8 text-center py-16">
          <div className="w-12 h-12 rounded-2xl bg-[#1A1A1A] border border-white/5 flex items-center justify-center text-zinc-500 mb-4">
            <FiBox className="w-6 h-6 text-zinc-500" />
          </div>
          <h3 className="text-white text-base font-bold mb-1">No Inventory Items</h3>
          <p className="text-zinc-500 text-sm max-w-sm">
            Add items or equipment to track them for this project.
          </p>
        </div>
      ) : (
        /* Items List */
        <div className="space-y-3">
          {projectInventory.map((item) => (
            <div
              key={item.id}
              className="bg-[#111111] border border-white/5 rounded-2xl p-4 flex items-center justify-between hover:border-white/10 transition-all"
            >
              <div className="flex items-center gap-4">
                {/* Box Icon Container */}
                <div className="w-10 h-10 rounded-xl bg-[#1A1A1A] border border-white/5 flex items-center justify-center text-zinc-400 shrink-0">
                  <FiBox className="w-4 h-4 text-zinc-400" />
                </div>
                <div>
                  <h4 className="text-white text-sm font-bold mb-1.5">{item.name}</h4>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="px-2 py-0.5 rounded bg-white/5 border border-white/5 text-zinc-400 text-[10px] font-medium">
                      {item.category}
                    </span>
                    {item.assetTag && (
                      <span className="px-2 py-0.5 rounded bg-[#18181B] border border-white/10 text-zinc-300 text-[10px] font-mono">
                        #{item.assetTag}
                      </span>
                    )}
                    <span
                      className={`px-2 py-0.5 rounded-full border text-[10px] font-semibold ${
                        item.condition === 'Excellent'
                          ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                          : item.condition === 'Good'
                            ? 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                            : item.condition === 'Fair'
                              ? 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                              : 'bg-rose-500/10 text-rose-400 border-rose-500/20'
                      }`}
                    >
                      {item.condition}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {item.budgetAllocation && (
                  <span className="text-zinc-400 text-xs font-semibold">
                    Budget: $
                    {parseFloat(item.budgetAllocation).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                )}
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => handleOpenEditInventoryModal(item)}
                    className="w-7 h-7 rounded-full bg-[#18181B] border border-white/5 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors cursor-pointer"
                  >
                    <FiEdit2 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleOpenDeleteInventoryModal(item)}
                    className="w-7 h-7 rounded-full bg-[#18181B] border border-white/5 flex items-center justify-center text-[#ef4444] hover:text-rose-400 hover:bg-zinc-800 transition-colors cursor-pointer"
                  >
                    <FiTrash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
